import { queryOptions } from '@tanstack/react-query'

// Public /public/quotes/{token} contract (USA-1028). Verified against the actual router by
// quote-contract.test.ts; independent of dashboard contracts. Type names drop the API's `Qb` prefix.

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'
const REQUEST_TIMEOUT_MS = 30_000

export type DecisionState = 'approved' | 'changes_requested' | 'pending' | 'rejected'
export type VisibleSection =
  'equipment' | 'fulfillment' | 'notes' | 'pricing' | 'services' | 'terms'

/** A photo or file on a comment (USA-1345); `url` is the signed API path that opens it. */
export type SharedCommentFile = {
  id: string
  filename: string
  content_type: string
  size_bytes: number
  kind: 'file' | 'image'
  url: string
}

export type SharedComment = {
  id: string
  author_name: string
  body: string
  line_position: number | null
  row_ref: string | null
  row_label: string | null
  created_at: string
  /** An answer from Sales (USA-1345). */
  from_staff: boolean
  files: SharedCommentFile[]
}

export type SharedAsset = {
  name: string
  url: string | null
}

export type SharedSpecification = {
  label: string
  value: string
  unit: string | null
}

export type SharedEquipment = {
  manufacturer: string
  model: string
  title: string
  primary_asset: SharedAsset | null
  specifications: SharedSpecification[]
}

export type SharedAlternatives = {
  current: SharedEquipment
  alternatives: SharedEquipment[]
}

export type SharedLabel = {
  label: string
}

export type SharedLeadTime = {
  minimum_days: number
  maximum_days: number
  label: string
}

export type SharedFulfillment = {
  availability: SharedLabel | null
  lead_time: SharedLeadTime | null
  freight: SharedLabel | null
}

/** A Safeware plan on the quote: the equipment it protects and the coverage it adds (USA-1162). */
export type SharedProtectionPlan = {
  covers: string
  term_months: number
  months_added: number
  /** Null when the manufacturer's warranty is not known. */
  manufacturer_warranty_months: number | null
  terms_url: string
}

export type SharedQuoteLine = {
  line_type: 'accessory' | 'configuration' | 'custom' | 'product' | 'service'
  row_ref: string
  parent_row_ref: string | null
  display_number: string
  position: number
  quantity: string
  description: string
  installation_for: string | null
  sell_amount: string | null
  extended_sell: string | null
  notes: string | null
  manufacturer: string | null
  model: string | null
  specifications: string[]
  spec_text: string | null
  spec_text_edited: boolean
  alternative_comparison: SharedAlternatives | null
  fulfillment: SharedFulfillment | null
  protection_plan: SharedProtectionPlan | null
  primary_asset: SharedAsset | null
}

export type SharedTotals = {
  equipment_subtotal: string
  services_subtotal: string
  other_subtotal: string
  sell_subtotal: string
  rebate: string
  tax: string
  freight: string
  grand_total: string
  deposit: string
  balance: string
}

export type SharedTerms = {
  balance_terms: string | null
  deposit_percent: string | null
  /** "prepaid" when the customer paid outside the online invoice. */
  term_type: string | null
}

export type SharedAddress = {
  name: string
  contact_name: string
  line1: string
  line2: string
  city: string
  state: string
  postal_code: string
  country: string
}

export type PublicInvoiceSummary = {
  status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void'
  number: string | null
  amount_due: string
  amount_paid: string
  amount_remaining: string
  paid_at: string | null
  /** A bank payment was made and is clearing. */
  payment_processing: boolean
  /** Our invoice page, where the customer pays (USA-1283). */
  pay_url: string | null
  document_url: string | null
  sent_at: string | null
}

export type SharedQuote = {
  quote_number: string
  job_reference: string | null
  title: string
  revision: number
  notes: string | null
  terms: SharedTerms | null
  lines: SharedQuoteLine[]
  totals: SharedTotals | null
  bill_to: SharedAddress | null
  deliver_to: SharedAddress | null
  invoice: PublicInvoiceSummary | null
}

export type SharedCustomer = {
  company_name: string
  contact_name: string
}

export type SharedSalesContact = {
  name: string
  email: string | null
}

/** The customer's signature approving this publication (USA-1285). */
export type SharedSignature = {
  signer_name: string
  signed_at: string
  publication_number: number
  source_revision: number
}

export type SharedPublication = {
  document_url: string
  publication_number: number
  source_revision: number
  mode: 'draft' | 'final'
  visible_sections: VisibleSection[]
  decision_state: DecisionState
  decision_name: string | null
  decided_at: string | null
  published_at: string
  signature: SharedSignature | null
}

export type SharedQuoteView = {
  publication: SharedPublication
  quote: SharedQuote
  customer: SharedCustomer
  sales_contact: SharedSalesContact | null
  comments: SharedComment[]
  expires_at: string
}

export class QuoteLinkError extends Error {
  status: number
  code: string
  constructor(status: number, code: string) {
    super(code)
    this.name = 'QuoteLinkError'
    this.status = status
    this.code = code
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      signal: init?.signal ?? controller.signal,
      headers: {
        Accept: 'application/json',
        ...(typeof init?.body === 'string' ? { 'Content-Type': 'application/json' } : {}),
      },
    })
    if (!response.ok) {
      let code = `request_failed_${response.status}`
      try {
        const body = (await response.json()) as {
          detail?: { code?: string } | string
        }
        code = (typeof body.detail === 'string' ? body.detail : body.detail?.code) || code
      } catch {
        // keep the status-based code
      }
      throw new QuoteLinkError(response.status, code)
    }
    return (await response.json()) as T
  } finally {
    clearTimeout(timer)
  }
}

const quotePath = (token: string, suffix = '') =>
  `/public/quotes/${encodeURIComponent(token)}${suffix}`

export const quoteLinkQueryOptions = (token: string) =>
  queryOptions({
    queryKey: ['customer-quote', token],
    queryFn: ({ signal }) => request<SharedQuoteView>(quotePath(token), { signal }),
    retry: (count, error) => !(error instanceof QuoteLinkError && error.status < 500) && count < 2,
  })

export function addQuoteComment(
  token: string,
  publication: number,
  authorName: string,
  body: string,
  rowRef: string | null,
  files: File[] = [],
) {
  const comment = JSON.stringify({
    expected_publication: publication,
    author_name: authorName,
    body,
    row_ref: rowRef,
  })
  if (!files.length) {
    return request<SharedComment>(quotePath(token, '/comments'), { method: 'POST', body: comment })
  }
  // Photos and files go as one upload with the comment (USA-1345).
  const form = new FormData()
  form.append('comment', comment)
  for (const file of files) form.append('files', file, file.name)
  return request<SharedComment>(quotePath(token, '/comments/files'), { method: 'POST', body: form })
}

export function decideQuote(
  token: string,
  revision: number,
  publication: number,
  name: string,
  decision: 'approved' | 'rejected' | 'changes_requested',
  comment: string,
) {
  return request<SharedQuoteView>(quotePath(token, '/decision'), {
    method: 'POST',
    body: JSON.stringify({
      expected_revision: revision,
      expected_publication: publication,
      name,
      decision,
      comment: comment || null,
    }),
  })
}

/** API paths in the response (the PDF, an invoice document) on this API's origin. */
export function apiUrl(path: string) {
  return `${API_BASE_URL}${path}`
}

/** An invoice PDF: our API path, or Stripe's own link for an invoice Stripe issued (before USA-1283). */
export function documentHref(url: string) {
  return /^https?:\/\//.test(url) ? url : apiUrl(url)
}
